import $ from 'jquery';
import { buildAttributes } from '../../../idea_portals/settings/common';

type saveKBOptions = {
  attributes?: Record<string, any>;
  fieldName?: string;
  configuration?: Record<string, any>;
  params?: Record<string, any>;
  onSuccess?: (response: any) => void;
  onError?: (response: any) => void;
  validate?: string;
};

// Updates the knowledge_base with attribute and value derived from the event. The event is also used to determine
// where to display the error and saving messages. Additional options can be provided:
// * attributes - Hash of attributes and values to use instead of the attribute/value derived from the event
// * params - Sent along with the request but are not part of the idea_portal attributes
// * onSuccess - Function to call on success
// * onError - Function to call on error
export function saveKB(event, options = {}) {
  let attributes = options['attributes'] || {};
  const fieldName = options['fieldName'] || 'knowledge_base';
  const configuration = options['configuration'] || {};
  const params = options['params'] || {};
  const validate = options['validate'];
  const onSuccess = options['onSuccess'];
  const onError = options['onError'];
  const errorElement = $(event.target.closest('.control'))
    .find('.errors')
    .text('');
  const warningElement = $(event.target.closest('.control'))
    .find('.warnings')
    .text('');
  const savingElement = saving(event);

  if (
    Object.keys(attributes).length === 0 &&
    Object.keys(configuration).length === 0
  ) {
    attributes = buildAttributes(event);
  }

  if (
    Object.keys(attributes).length > 0 &&
    Object.keys(configuration).length > 0
  ) {
    // Don't support updating both - the server is only going to save one.
    throw new Error('Cannot specify both attributes and configuration');
  }

  $.ajax({
    url: location.href,
    type: 'PUT',
    data: JSON.stringify({
      [fieldName]: attributes,
      knowledge_base_configuration: configuration,
      ...params,
      validate,
    }),
    dataType: 'json',
    contentType: 'application/json',
    success: response => {
      saved(savingElement);
      if (onSuccess) onSuccess(response);
      if (response.warning) warningElement.text(response.warning);
    },
    error: response => {
      savingElement.text('');
      if (response.responseJSON?.errors)
        errorElement.text(response.responseJSON.errors[0]);
      else
        errorElement.text(
          'Something went wrong. Please try again, or contact support@aha.io for assistance.'
        );
      if (onError) onError(response);
    },
  });
}

// Message to show while attempting to save
export function saving(event, message = 'Saving…') {
  // A panel may have multiple controls (and therefore multiple "Saving" elements), so we look for the closest one.
  return $(event.target.closest(':is(.control, .panel):has(.saving)'))
    .find('.saving:last')
    .stop()
    .css('opacity', 1)
    .addClass('pull-right')
    .text(message);
}

// Message to show after successful save
export function saved(savingElement, message = 'Saved!') {
  savingElement.text(message).fadeTo(3000, 0, () => savingElement.text(''));
}

/**
 * Automatically register fields for autosave if they have the
 * `data-kb-autosave` attribute, and optionally the `data-kb-config-key`
 * attribute to specify the configuration key to save to. If the config key
 * isn't specified, the element id will be split into a group and field on the
 * first underscore, and joined with a '.' to form the configuration key.
 */
export function watchAutosaveFields(options: saveKBOptions = {}) {
  $<HTMLInputElement>('[data-kb-autosave]').each((i, element) => {
    const { id, type } = element;
    const isConfig = element.getAttribute('data-kb-autosave') === 'config';

    // Get the configuration key from the element attributes, or fall back to
    // splitting the element id into a group and field
    // i.e. homepage_enabled => ['homepage', 'enabled'] => 'homepage.enabled'
    const configurationKey =
      element.getAttribute('data-kb-config-key') ??
      id.split(/_(.*)/, 2).join('.');

    $(element).on('change', event => {
      if (isConfig) {
        options.configuration = {
          [configurationKey]:
            type === 'checkbox' ? event.target.checked : event.target.value,
        };
      }

      // Validate the data before saving if necessary.
      if (element.tagName === 'INPUT' && element.type === 'url') {
        options.validate = 'url';
      }

      saveKB(event, options);
    });
  });
}

export function sortableList({
  listClass,
  fieldName,
}: {
  listClass: string;
  fieldName?: string | null;
}) {
  $(listClass).sortable({
    handle: '.move-handle',
    placeholder: 'task-template placeholder', // placeholder for the sortable list
    items: 'li.bubble-list-element',
    start() {
      // Make sure that we stop editing before dragging.
      if (
        document.activeElement &&
        document.activeElement instanceof HTMLElement
      ) {
        return document.activeElement.blur();
      }
    },
    beforeStop(event, ui) {
      const $item = $(ui.item);

      const url = $item.attr('data-url');
      const currentPosition = parseInt($item.attr('data-position') || '-1', 10);

      // Set the new position to the position of the previous item + 1.
      let newPosition: string | number =
        $item.prevAll('li').first().attr('data-position') || '-1';

      if (!newPosition) {
        newPosition = 1;
      } else {
        newPosition = parseInt(newPosition, 10);
        // The rank comparison is to workaround a bug in acts_as_list.
        if (currentPosition > newPosition) {
          newPosition = newPosition + 1;
        }
      }

      const errorElement = $(event.target)
        .closest('.control')
        .find('.errors')
        .text('');
      const savingElement = saving(event);

      const data = {};

      if (fieldName) {
        data[fieldName] = {
          position: newPosition,
        };
      } else {
        data['position'] = newPosition;
      }

      return $.ajax({
        type: 'PUT',
        url: url,
        data,
        dataType: 'script',
        success: () => {
          saved(savingElement);
        },
        error: response => {
          savingElement.text('');
          if (response.responseJSON?.errors)
            errorElement.text(response.responseJSON.errors[0]);
          else
            errorElement.text(
              'Something went wrong. Please try again, or contact support@aha.io for assistance.'
            );
        },
      });
    },
  });
}

// Normalize whitespace and quote characters in a CSS selector to make it easier to compare against other selectors.
function normalizeSelector(selector: string): string {
  return selector.replace(/\s+/g, ' ').replace(/"([^"]*)"/g, "'$1'");
}

// This function takes a selector for a <style> tag, a selector for a CSS rule within that style tag, a CSS variable name, and a value, and sets the value of the CSS variable in the rule, and then updates the text content of the style tag to match the new state of the stylesheet. Uses CSSOM to manipulate the stylesheet, as this is more precise than using string manipulation.
export function setCSSVariableInStyleTag(
  styleSelector: string,
  selector: string,
  variable: string,
  value: string
): void {
  const styleElement = document.querySelector(styleSelector);
  if (!(styleElement instanceof HTMLStyleElement) || !styleElement.sheet) {
    throw new Error(
      `Style tag with id ${styleSelector} not found or has no associated stylesheet.`
    );
  }

  const sheet = styleElement.sheet;
  const rules = sheet?.cssRules;

  if (!rules) return;

  const normalizedSelector = normalizeSelector(selector);
  const rule = Array.from(rules).find((rule): rule is CSSStyleRule => {
    return (
      rule instanceof CSSStyleRule &&
      normalizeSelector(rule.selectorText) === normalizedSelector
    );
  });

  if (rule) {
    rule.style.setProperty(variable, value.toString());

    // Update the style element's text content to match. Note that while technically we don't need to actually update the contents of the style element, it's a good idea to keep the two in sync as it makes for easier debugging if something goes wrong.
    const cssText = Array.from(rules)
      .map(rule => rule.cssText)
      .join('\n');
    styleElement.textContent = cssText;
  } else {
    throw new Error(`Rule with selector ${selector} not found in stylesheet.`);
  }
}
